import { supabase } from "@/lib/supabase";

async function workspaceHeaders(orgId: string): Promise<HeadersInit> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");
  headers.Authorization = `Bearer ${token}`;
  headers["X-Workspace-Organization-Id"] = orgId;
  return headers;
}

export type PricingLaneMode = "auto" | "sales_csv" | "manufacturer_inherit";

export type SalesCatalogItemRow = {
  id: string;
  product_line: string;
  width_ft: number;
  length_ft: number;
  package_key: string | null;
  options_key: string | null;
  external_product_id: string | null;
  msrp: number;
  currency: string;
  effective_at: string | null;
  expires_at: string | null;
  source_row_number: number | null;
};

export type ManufacturerCatalogItemRow = {
  id: string;
  product_line: string;
  width_ft: number;
  length_ft: number;
  package_key: string | null;
  options_key: string | null;
  external_product_id: string | null;
  msrp: number;
  currency: string;
  effective_at: string | null;
  expires_at: string | null;
  updated_at?: string | null;
};

/** Filled when user picks a manufacturer row to prefill the subscriber override form. */
export type SubscriberOverridePrefill = {
  revision: number;
  product_line: string;
  width_ft: number;
  length_ft: number;
  package_key: string | null;
  options_key: string | null;
  base_msrp: number;
  currency: string;
};

export type SalesOrgPricingGetResponse = {
  ok: true;
  organization_id: string;
  settings: { pricing_lane_mode: PricingLaneMode; linked_manufacturer_org_id: string | null };
  sales_snapshot: {
    tablesAvailable?: boolean;
    has_active: boolean;
    version: number | null;
    row_count: number;
    published_at: string | null;
  };
  manufacturer_links: Array<{ id: string; name: string }>;
  overrides: unknown[];
};

export async function fetchSalesOrgPricing(orgId: string): Promise<SalesOrgPricingGetResponse> {
  const r = await fetch("/api/admin/sales-org-pricing", { method: "GET", headers: await workspaceHeaders(orgId) });
  const j = (await r.json()) as SalesOrgPricingGetResponse & { ok?: boolean; error?: string };
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j as SalesOrgPricingGetResponse;
}

export async function postSalesOrgPricingOp(orgId: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const r = await fetch("/api/admin/sales-org-pricing", {
    method: "POST",
    headers: await workspaceHeaders(orgId),
    body: JSON.stringify(body),
  });
  const j = (await r.json()) as Record<string, unknown> & { ok?: boolean; error?: string };
  if (!r.ok || j.ok === false) throw new Error((j.error as string) || `HTTP ${r.status}`);
  return j;
}
